import { useCallback, useEffect, useMemo, useState } from 'react'
import type { TFunction } from 'i18next'
import { adminApi } from '../../services'
import type { OrgCasesByOwner, OrgCasesListResponse } from '../../types/dashboard'
import type { CaseQuotaStatus, ClientProfile, CreateCaseInput } from '../../types/case-management'
import { useCaseQuota, useClientProfileMutations, useClientProfiles } from '../../hooks'
import {
  useCaseClickHandler,
  useCreateCaseHandler,
  useCreateClientHandler,
  useToggleOwner,
} from './use-org-cases-handlers'

interface UseOrgCasesPageArgs {
  t: TFunction<'cases'>
}

interface OrgCasesPageState {
  data: OrgCasesListResponse | null
  loading: boolean
  error: string | null
  expandedOwners: Set<string>
  searchQuery: string
  isNewCaseModalOpen: boolean
  creating: boolean
  quota: CaseQuotaStatus | null
  clients: ClientProfile[]
  creatingClient: boolean
  filteredOwners: OrgCasesByOwner[]
  setSearchQuery: (value: string) => void
  setIsNewCaseModalOpen: (open: boolean) => void
  fetchData: () => Promise<void>
  toggleOwner: (ownerId: string) => void
  handleCaseClick: (caseId: string) => void
  handleCreateCase: (input: CreateCaseInput) => Promise<void>
  handleCreateClient: (input: { name: string; shortName?: string }) => Promise<ClientProfile | null>
}

function filterOwners(owners: OrgCasesByOwner[], query: string): OrgCasesByOwner[] {
  if (!query.trim()) return owners
  const lower = query.toLowerCase()
  return owners.filter((owner) => {
    if (owner.ownerName.toLowerCase().includes(lower)) return true
    return owner.cases.some(
      (caseItem) =>
        caseItem.name.toLowerCase().includes(lower) ||
        (caseItem.clientName?.toLowerCase().includes(lower) ?? false) ||
        (caseItem.orgCaseReference?.toLowerCase().includes(lower) ?? false)
    )
  })
}

export function useOrgCasesPage({ t }: UseOrgCasesPageArgs): OrgCasesPageState {
  const [data, setData] = useState<OrgCasesListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedOwners, setExpandedOwners] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [isNewCaseModalOpen, setIsNewCaseModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  const { quota, refetch: refetchQuota } = useCaseQuota()
  const { profiles: clients, refetch: refetchClients } = useClientProfiles()
  const { create: createClient, creating: creatingClient } = useClientProfileMutations()

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await adminApi.listOrgCases()
      setData(result)
      setExpandedOwners(new Set(result.byOwner.map((owner) => owner.ownerId)))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('orgCases.failedToLoadCases'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const toggleOwner = useToggleOwner({ setExpandedOwners })
  const handleCaseClick = useCaseClickHandler()

  const handleCreateCase = useCreateCaseHandler({
    fetchData,
    refetchQuota,
    setCreating,
    setIsNewCaseModalOpen,
  })

  const handleCreateClient = useCreateClientHandler({ createClient, refetchClients })

  const filteredOwners = useMemo<OrgCasesByOwner[]>(
    () => filterOwners(data?.byOwner ?? [], searchQuery),
    [data, searchQuery]
  )

  return {
    data,
    loading,
    error,
    expandedOwners,
    searchQuery,
    isNewCaseModalOpen,
    creating,
    quota,
    clients,
    creatingClient,
    filteredOwners,
    setSearchQuery,
    setIsNewCaseModalOpen,
    fetchData,
    toggleOwner,
    handleCaseClick,
    handleCreateCase,
    handleCreateClient,
  }
}
